import React, { memo } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopy from '@mui/icons-material/ContentCopy';
import PartnerAvatar from './PartnerAvatar';
import { JOB_KIND_META } from './jobKindMeta';
import { JobKind, formatCreated, shortId, userHandle } from './jobModel';
import { monoSx } from './styles';

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export const JobIdCell = memo(function JobIdCell({
  id,
  userId,
  onCopy,
}: {
  id: string;
  userId?: string;
  onCopy: (id: string) => void;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography component="span" title={id} sx={{ ...monoSx, fontSize: 13, fontWeight: 500, color: 'text.primary' }}>
          {shortId(id)}
        </Typography>
        <Tooltip title="Copy full job ID">
          <IconButton
            size="small"
            aria-label="Copy full job ID"
            onClick={(e) => {
              stop(e);
              onCopy(id);
            }}
            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
          >
            <ContentCopy sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {userId ? (
        <Typography noWrap title={userId} sx={{ fontSize: 12, color: 'text.secondary' }}>
          by {userHandle(userId)}
        </Typography>
      ) : null}
    </Box>
  );
});

export const PartnerCell = memo(function PartnerCell({ name }: { name: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <PartnerAvatar name={name} />
      <Typography noWrap title={name} sx={{ fontWeight: 500, fontSize: 14, color: 'text.primary' }}>
        {name}
      </Typography>
    </Box>
  );
});

export const JobTypeCell = memo(function JobTypeCell({
  kind,
  label,
  detail,
}: {
  kind: JobKind;
  label: string;
  detail: string | null;
}) {
  const { Icon } = JOB_KIND_META[kind];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Icon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
        {detail ? (
          <Typography noWrap title={detail} sx={{ fontSize: 12, color: 'text.disabled' }}>
            {detail}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
});

export const CreatedCell = memo(function CreatedCell({ createdAt }: { createdAt: number }) {
  const { day, time } = formatCreated(createdAt);
  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: 'text.primary' }}>{day}</Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{time}</Typography>
    </Box>
  );
});
